import * as m from '@staiter/i18n';
import {
  AUTH_KIND_FIELDS,
  type AuthKind,
  type Connection,
  type ConnectorDescriptor,
  type TestConnectionResult,
} from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Plug2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, ConfirmDialog, Dialog, Input, WizardStepper } from '~/components/primitives';
import {
  createConnection,
  deleteConnection,
  fetchCatalog,
  fetchConnections,
  testConnection,
} from '~/lib/api/integrations-platform';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.integrations_.platform.module.css';
import admin from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/integrations_/platform')({
  staticData: { minMainWidth: 960 },
  component: IntegrationsPlatform,
});

const PASSWORD_FIELDS = new Set(['api_token', 'password', 'client_secret']);
// Поля, для которых есть подсказка «где взять» (self-service без разработчика).
const HINT_FIELDS = new Set(['webhook_url', 'api_token']);

function fieldType(field: string): 'text' | 'password' {
  return PASSWORD_FIELDS.has(field) ? 'password' : 'text';
}

function formatTs(ts: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function HealthBadge({ health }: { health: Connection['health'] }): JSX.Element {
  const cls =
    health === 'ok'
      ? admin.healthOk
      : health === 'degraded'
        ? admin.healthWarn
        : admin.healthDanger;
  return (
    <span className={[admin.healthBadge, cls].join(' ')}>
      {m.integration_health_label({ health })}
    </span>
  );
}

function IntegrationsPlatform(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [disableTarget, setDisableTarget] = useState<Connection | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['integrations', 'platform', 'connections'],
    queryFn: fetchConnections,
  });

  const testMut = useMutation({
    mutationFn: (id: string) => testConnection(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_platform_test_ok() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'platform', 'connections'] });
    },
    onError: () => toast.push({ variant: 'error', title: m.integ_platform_test_failed() }),
  });

  const disableMut = useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_platform_toast_disabled() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'platform', 'connections'] });
      setDisableTarget(null);
    },
  });

  const items = data?.items ?? [];

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.integ_platform_title()}
        description={m.integ_platform_desc()}
        actions={
          <Button
            size="sm"
            variant="secondary"
            leadingIcon={<Plus size={14} />}
            onClick={() => setWizardOpen(true)}
          >
            {m.integ_platform_add()}
          </Button>
        }
      />

      <Table
        ariaLabel={m.integ_platform_title()}
        loading={isLoading}
        data={items}
        rowKey={(c) => c.id}
        emptyTitle={m.integ_platform_empty_title()}
        emptyDescription={m.integ_platform_empty_desc()}
        emptyAction={
          <Button size="sm" leadingIcon={<Plus size={14} />} onClick={() => setWizardOpen(true)}>
            {m.integ_platform_add()}
          </Button>
        }
        columns={[
          {
            id: 'provider',
            header: m.integ_platform_col_provider(),
            accessor: (c) => (
              <span className={styles.providerCell}>
                <Plug2 size={13} aria-hidden="true" /> {c.provider_key}
              </span>
            ),
          },
          {
            id: 'status',
            header: m.integ_platform_col_status(),
            accessor: (c) => (
              <span
                className={[
                  styles.statusBadge,
                  c.status === 'active' ? styles.statusActive : styles.statusDisabled,
                ].join(' ')}
              >
                {m.integ_platform_status_label({ status: c.status })}
              </span>
            ),
          },
          {
            id: 'health',
            header: m.integ_platform_col_health(),
            accessor: (c) => <HealthBadge health={c.health} />,
          },
          {
            id: 'schedule',
            header: m.integ_platform_col_schedule(),
            accessor: (c) => m.integ_platform_schedule_label({ schedule: c.schedule }),
          },
          {
            id: 'last_pull',
            header: m.integ_platform_col_last_pull(),
            accessor: (c) => <span className={admin.mono}>{formatTs(c.last_pull_at)}</span>,
          },
          {
            id: 'actions',
            header: '',
            accessor: (c) => (
              <div className={admin.actionsCell}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    navigate({ to: '/admin/integrations/connections/$id', params: { id: c.id } })
                  }
                >
                  {m.integ_detail_overview()}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<RefreshCw size={12} />}
                  onClick={() => testMut.mutate(c.id)}
                  disabled={testMut.isPending}
                >
                  {m.integ_platform_action_test()}
                </Button>
                {c.status === 'active' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leadingIcon={<Trash2 size={12} />}
                    onClick={() => setDisableTarget(c)}
                  >
                    {m.integ_platform_action_disable()}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {disableTarget && (
        <ConfirmDialog
          open
          onCancel={() => setDisableTarget(null)}
          onConfirm={() => disableMut.mutate(disableTarget.id)}
          title={m.integ_platform_disable_confirm_title()}
          description={m.integ_platform_disable_confirm_desc()}
          confirmLabel={m.integ_platform_action_disable()}
          cancelLabel={m.common_button_cancel()}
          variant="danger"
          loading={disableMut.isPending}
        />
      )}

      <ConnectWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onDone={() => {
          setWizardOpen(false);
          void qc.invalidateQueries({ queryKey: ['integrations', 'platform', 'connections'] });
        }}
      />
    </div>
  );
}

type WizardStepId = 'provider' | 'creds' | 'test' | 'done';

function ConnectWizard({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [step, setStep] = useState<WizardStepId>('provider');
  const [selected, setSelected] = useState<ConnectorDescriptor | null>(null);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<Connection | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  const catalog = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: fetchCatalog,
    enabled: open,
  });

  const reset = (): void => {
    setStep('provider');
    setSelected(null);
    setCreds({});
    setCreated(null);
    setTestResult(null);
  };
  const close = (): void => {
    reset();
    onClose();
  };

  const createMut = useMutation({
    mutationFn: () => createConnection({ provider_key: selected!.key, credentials: creds }),
    onSuccess: async (conn) => {
      setCreated(conn);
      setStep('test');
      toast.push({ variant: 'success', title: m.integ_platform_toast_created() });
      try {
        const r = await testConnection(conn.id);
        setTestResult(r);
      } catch {
        setTestResult({ health: 'degraded', reason_code: 'integration.test_failed' });
      }
    },
    onError: () => toast.push({ variant: 'error', title: m.integ_platform_toast_create_failed() }),
  });

  const fields: readonly string[] = selected
    ? AUTH_KIND_FIELDS[selected.auth_kind as AuthKind]
    : [];
  const credsComplete =
    fields.length > 0 && fields.every((f) => (creds[f] ?? '').trim().length > 0);

  const steps = [
    { n: 1, label: m.integ_platform_step_provider() },
    { n: 2, label: m.integ_platform_step_creds() },
    { n: 3, label: m.integ_platform_step_test() },
    { n: 4, label: m.integ_platform_step_done() },
  ];
  const currentN = step === 'provider' ? 1 : step === 'creds' ? 2 : step === 'test' ? 3 : 4;

  return (
    <Dialog
      open={open}
      onClose={close}
      title={m.integ_platform_wizard_title()}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {m.common_button_cancel()}
          </Button>
          {step === 'creds' && (
            <>
              <Button variant="secondary" onClick={() => setStep('provider')}>
                {m.common_button_back()}
              </Button>
              <Button
                onClick={() => createMut.mutate()}
                disabled={!credsComplete || createMut.isPending}
              >
                {createMut.isPending ? m.integ_platform_testing() : m.integ_platform_btn_create()}
              </Button>
            </>
          )}
          {step === 'test' && (
            <Button onClick={() => setStep('done')}>{m.common_button_next()}</Button>
          )}
          {step === 'done' && <Button onClick={onDone}>{m.common_button_close()}</Button>}
        </>
      }
    >
      <WizardStepper steps={steps} current={currentN} ariaLabel={m.integ_platform_wizard_title()} />

      {step === 'provider' && (
        <div className={styles.catalogGrid}>
          {(catalog.data?.items ?? []).map((d) => (
            <button
              key={d.key}
              type="button"
              className={[
                styles.catalogCard,
                selected?.key === d.key ? styles.catalogCardSelected : '',
              ].join(' ')}
              disabled={!d.available}
              onClick={() => {
                setSelected(d);
                setCreds({});
                setStep('creds');
              }}
            >
              <span className={styles.catalogName}>{d.name}</span>
              {!d.available && (
                <span className={styles.comingSoon}>{m.integ_platform_coming_soon()}</span>
              )}
              <span className={styles.catalogCaps}>
                {d.capabilities.map((cap) => (
                  <span key={cap} className={styles.capChip}>
                    {m.integ_platform_capability_label({ capability: cap })}
                  </span>
                ))}
              </span>
              {d.multi_entity && (
                <span className={styles.catalogMeta}>{m.integ_platform_multi_entity()}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {step === 'creds' && selected && (
        <div>
          <p className={styles.credsHint}>{m.integ_platform_creds_hint()}</p>
          {fields.map((f) => (
            <div key={f} className={styles.formRow}>
              <Input
                label={m.integ_platform_field_label({ field: f })}
                type={fieldType(f)}
                value={creds[f] ?? ''}
                onChange={(e) => setCreds((s) => ({ ...s, [f]: e.target.value }))}
                autoComplete="off"
                {...(HINT_FIELDS.has(f) ? { hint: m.integ_platform_hint_label({ field: f }) } : {})}
              />
            </div>
          ))}
        </div>
      )}

      {step === 'test' && (
        <div
          className={[styles.testResult, testResult?.health === 'ok' ? styles.testOk : ''].join(
            ' ',
          )}
        >
          {!testResult && <span>{m.integ_platform_testing()}</span>}
          {testResult?.health === 'ok' && (
            <>
              <span className={styles.testRow}>
                <CheckCircle2 size={15} aria-hidden="true" /> {m.integ_platform_test_ok()}
              </span>
              {typeof testResult.counterparties_total === 'number' && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_counterparties({ count: testResult.counterparties_total })}
                </span>
              )}
              {typeof testResult.operations_total === 'number' && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_operations({ count: testResult.operations_total })}
                </span>
              )}
              {Array.isArray(testResult.external_entities) && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_entities({ count: testResult.external_entities.length })}
                </span>
              )}
            </>
          )}
          {testResult && testResult.health !== 'ok' && (
            <span className={styles.testRow}>{m.integ_platform_test_failed()}</span>
          )}
        </div>
      )}

      {step === 'done' && (
        <div className={styles.doneWrap}>
          <CheckCircle2 size={36} aria-hidden="true" />
          <span className={styles.doneTitle}>{m.integ_platform_done_title()}</span>
          <span className={styles.doneDesc}>{m.integ_platform_done_desc()}</span>
        </div>
      )}
    </Dialog>
  );
}
